import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, Share2, Trophy, Target, Users } from "lucide-react";
import { usePublicProfile } from "@/hooks/use-public-profile";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { LanguageSelector } from "@/components/LanguageSelector";
import { DOMAIN_COLORS, DOMAIN_LABELS_AR } from "@/components/StrengthCard/constants";

const getDomainStyles = (domain: string) => {
    switch (domain) {
        case "Strategic Thinking":
            return {
                badge: "bg-primary/10 text-primary border-primary/25",
                border: "border-l-primary",
            };
        case "Executing":
            return {
                badge: "bg-secondary/10 text-brand-green-dark border-secondary/25",
                border: "border-l-secondary",
            };
        case "Influencing":
            return {
                badge: "bg-warning/10 text-warning border-warning/25",
                border: "border-l-warning",
            };
        case "Relationship Building":
            return {
                badge: "bg-brand-teal-dark/10 text-brand-teal-dark border-brand-teal-dark/25",
                border: "border-l-brand-teal-dark",
            };
        default:
            return {
                badge: "bg-secondary/20 text-muted-foreground",
                border: "border-l-muted",
            };
    }
};

export default function PublicProfile() {
    const { userId } = useParams();
    const { getPublicProfile, isLoading } = usePublicProfile();
    const { t, i18n } = useTranslation();

    const [data, setData] = useState<any>(null);
    const isArabic = i18n.language === 'ar';

    useEffect(() => {
        if (!userId) return;

        const fetchData = async () => {
            const res = await getPublicProfile(userId);
            if (res.success) setData(res.data);
        };

        fetchData();
    }, [userId]);

    if (isLoading || !data) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background relative overflow-hidden">
                <div className="flex flex-col items-center gap-4 z-10">
                    <Loader2 className="w-12 h-12 animate-spin text-primary" />
                    <p className="text-muted-foreground animate-pulse">{t('publicProfile.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute inset-0 gradient-hero" />
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#1E7A8C0D_1px,transparent_1px),linear-gradient(to_bottom,#5CB85C0D_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_110%)]" />
            </div>

            {/* Header with Logo */}
            <div className="relative z-10 border-b border-border bg-card/90 backdrop-blur-xl shadow-sm">
                <div className="container mx-auto max-w-6xl px-4 py-4 sm:px-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <Link to="/" className="transition-transform hover:scale-105">
                            <Logo size="sm" />
                        </Link>
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                            <LanguageSelector variant="dark" />
                            <Link to="/auth">
                                <Button variant="outline" size="sm" className="gap-2 transition-all">
                                    <Trophy className="w-4 h-4" />
                                    <span className="hidden sm:inline">{t('publicProfile.discoverYourStrengths')}</span>
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto max-w-4xl px-4 py-8 relative z-10 sm:px-6 sm:py-12">
                {/* Profile Header Card */}
                <Card className="mb-8 overflow-hidden border-border shadow-card bg-card/95 backdrop-blur-xl">
                    <CardContent className="pt-8 pb-8 relative">
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                            <Avatar className="h-24 w-24 shadow-2xl ring-4 ring-primary/20 transition-transform hover:scale-105">
                                <AvatarFallback className="maher-gradient text-white text-3xl font-bold">
                                    {data.name?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>

                            <div className="flex-1 text-center md:text-left">
                                <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start mb-2">
                                    <h1 className="break-words text-3xl font-bold text-gradient sm:text-4xl">
                                        {data.name}
                                    </h1>
                                    {data.isPublic && (
                                        <Badge className="status-success gap-1">
                                            <Share2 className="w-3 h-3" />
                                            {t('publicProfile.public')}
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-muted-foreground text-sm mb-4 flex items-center gap-2 justify-center md:justify-start">
                                    <Target className="w-4 h-4" />
                                    {t('publicProfile.cliftonStrengthsProfile')}
                                </p>
                                
                                {/* Top Domains Badges */}
                                {data.isPublic && data.topDomains && data.topDomains.length > 0 && (
                                    <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                                        {data.topDomains.map((domain: any) => {
                                            const color = DOMAIN_COLORS[domain.domain] || "#6B7280";
                                            const domainName = isArabic ? (DOMAIN_LABELS_AR[domain.domain] || domain.domain) : domain.domain;
                                            return (
                                                <Badge
                                                    key={domain.domain}
                                                    className="gap-2 px-3 py-1 border transition-all hover:scale-105"
                                                    style={{
                                                        backgroundColor: `${color}15`,
                                                        borderColor: `${color}40`,
                                                        color: color,
                                                    }}
                                                >
                                                    <div
                                                        className="w-2 h-2 rounded-full"
                                                        style={{ backgroundColor: color }}
                                                    />
                                                    {domainName}
                                                    <span className="font-semibold ml-1">×{domain.count}</span>
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Private Profile Message */}
                {!data.isPublic && (
                    <Card className="border-2 border-dashed border-border shadow-card bg-card/95 backdrop-blur-xl">
                        <CardContent className="py-16 text-center">
                            <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
                                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                                    <Lock className="w-10 h-10 text-muted-foreground" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold mb-2 text-foreground">{t('publicProfile.privateProfile')}</h3>
                                    <p className="text-muted-foreground">
                                        {t('publicProfile.privateProfileDesc')}
                                    </p>
                                </div>
                                <Link to="/auth" className="mt-4">
                                    <Button className="gap-2">
                                        <Trophy className="w-4 h-4" />
                                        {t('publicProfile.createYourProfile')}
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Public Profile - Strengths List */}
                {data.isPublic && (
                    <Card className="border-border shadow-card overflow-hidden bg-card/95 backdrop-blur-xl">
                        <CardHeader className="gradient-hero border-b border-border">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <CardTitle className="text-xl font-bold flex items-center gap-2 text-foreground sm:text-2xl">
                                    <Trophy className="w-6 h-6 text-primary" />
                                    {t('publicProfile.topStrengths')}
                                </CardTitle>
                                <Badge variant="outline" className="gap-1">
                                    <Users className="w-3 h-3" />
                                    {data.strengths?.length || 0} {t('publicProfile.strengths')}
                                </Badge>
                            </div>
                        </CardHeader>

                        <CardContent className="p-4 sm:p-6">
                            <div className="grid gap-4">
                                {data.strengths?.map((s: any, index: number) => {
                                    const styles = getDomainStyles(s.domain);
                                    const color = DOMAIN_COLORS[s.domain] || "#6B7280";

                                    return (
                                        <div
                                            key={s.rank}
                                            className={`group relative flex min-w-0 items-center gap-3 rounded-lg border border-l-[6px] p-4 ${styles.border} bg-card hover:border-primary/25 hover:shadow-card hover:scale-[1.01] transition-all duration-300 cursor-pointer sm:gap-4 sm:p-5`}
                                            style={{
                                                animationDelay: `${index * 50}ms`,
                                                animation: 'fadeInUp 0.5s ease-out forwards',
                                            }}
                                        >
                                            {/* Rank Badge */}
                                            <div
                                                className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl text-lg font-bold border-2 shadow-sm group-hover:scale-110 transition-transform"
                                                style={{
                                                    backgroundColor: `${color}15`,
                                                    borderColor: `${color}40`,
                                                    color: color,
                                                }}
                                            >
                                                {s.rank}
                                            </div>

                                            {/* Strength Info */}
                                            <div className="flex-1 min-w-0">
                                                <p className="break-words text-base font-bold mb-1 text-foreground group-hover:text-primary transition-colors sm:text-lg">
                                                    {isArabic && s.nameAr ? s.nameAr : s.name}
                                                </p>
                                                <Badge
                                                    className="gap-1.5 border"
                                                    style={{
                                                        backgroundColor: `${color}10`,
                                                        borderColor: `${color}30`,
                                                        color: color,
                                                    }}
                                                >
                                                    <div
                                                        className="w-1.5 h-1.5 rounded-full"
                                                        style={{ backgroundColor: color }}
                                                    />
                                                    {isArabic ? (DOMAIN_LABELS_AR[s.domain] || s.domain) : s.domain}
                                                </Badge>
                                            </div>

                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Footer */}
            <div className="border-t border-border bg-card/90 backdrop-blur-xl mt-16 relative z-10 shadow-sm">
                <div className="container mx-auto max-w-6xl px-4 py-8 sm:px-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <Logo size="sm" />
                            <span className="text-sm text-muted-foreground">
                                {t('publicProfile.poweredBy')}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                            <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                                {t('nav.home')}
                            </Link>
                            <span className="text-muted-foreground">•</span>
                            <Link to="/auth" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                                {t('nav.getStarted')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes float {
                    0%, 100% {
                        transform: translate(0, 0) scale(1);
                    }
                    33% {
                        transform: translate(30px, -30px) scale(1.1);
                    }
                    66% {
                        transform: translate(-20px, 20px) scale(0.9);
                    }
                }
                
                @keyframes float-delayed {
                    0%, 100% {
                        transform: translate(0, 0) scale(1);
                    }
                    33% {
                        transform: translate(-30px, 30px) scale(1.1);
                    }
                    66% {
                        transform: translate(20px, -20px) scale(0.9);
                    }
                }
                
                @keyframes pulse-slow {
                    0%, 100% {
                        opacity: 0.3;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 0.5;
                        transform: scale(1.05);
                    }
                }
                
                @keyframes float-particle {
                    0%, 100% {
                        transform: translate(0, 0);
                        opacity: 0.3;
                    }
                    25% {
                        transform: translate(10px, -20px);
                        opacity: 0.6;
                    }
                    50% {
                        transform: translate(20px, -10px);
                        opacity: 0.3;
                    }
                    75% {
                        transform: translate(10px, 10px);
                        opacity: 0.5;
                    }
                }
                
                @keyframes float-particle-delayed {
                    0%, 100% {
                        transform: translate(0, 0);
                        opacity: 0.2;
                    }
                    25% {
                        transform: translate(-15px, 20px);
                        opacity: 0.5;
                    }
                    50% {
                        transform: translate(-25px, 10px);
                        opacity: 0.3;
                    }
                    75% {
                        transform: translate(-10px, -10px);
                        opacity: 0.4;
                    }
                }
                
                @keyframes grid-flow {
                    0% {
                        transform: translateY(0);
                    }
                    100% {
                        transform: translateY(32px);
                    }
                }
                
                @keyframes gradient-shift {
                    0%, 100% {
                        opacity: 0.3;
                    }
                    50% {
                        opacity: 0.6;
                    }
                }
                
                .animate-float {
                    animation: float 20s ease-in-out infinite;
                }
                
                .animate-float-delayed {
                    animation: float-delayed 25s ease-in-out infinite;
                }
                
                .animate-pulse-slow {
                    animation: pulse-slow 4s ease-in-out infinite;
                }
                
                .animate-float-particle {
                    animation: float-particle 8s ease-in-out infinite;
                }
                
                .animate-float-particle-delayed {
                    animation: float-particle-delayed 10s ease-in-out infinite;
                }
                
                .animate-grid-flow {
                    animation: grid-flow 2s linear infinite;
                }
                
                .animate-gradient-shift {
                    animation: gradient-shift 8s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
}
